// One place to turn a FastAPI error body into display text.
//
// API errors carry a stable code next to the English text
// (``{"detail": "file not found", "error": "file_not_found",
// "params": {...}}``, services/api/src/sheafworks_api/errors.py): the
// message shown is ``errors.<code>`` from the UI catalogue, in the
// user's language, and the server's English ``detail`` when the
// catalogue has no such code (an older UI, or an error without one).
//
// FastAPI sends ``detail`` either as a string (HTTPException) or, for
// request-validation failures (422), as a list of
// ``{loc, msg, type, ...}`` objects. Rendering the list directly crashed
// React ("Objects are not valid as a React child").

import i18n from "@/i18n";

/** Human-readable message from an error body, or ``fallback``. */
export function errorDetail(body: unknown, fallback: string): string {
  if (typeof body === "string") return body;
  if (body === null || typeof body !== "object") return fallback;
  const { error, params } = body as { error?: unknown; params?: unknown };
  if (typeof error === "string" && i18n.exists(`errors.${error}`)) {
    const values = params && typeof params === "object" ? params : {};
    return i18n.t(`errors.${error}`, values as Record<string, unknown>);
  }
  if (!("detail" in body)) return fallback;
  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const { msg, loc } = item as { msg?: unknown; loc?: unknown };
        if (typeof msg !== "string") return null;
        // loc is like ["body", "email"]; name the field when there is one.
        const field = Array.isArray(loc)
          ? loc.filter((p) => p !== "body").at(-1)
          : undefined;
        return typeof field === "string" ? `${field}: ${msg}` : msg;
      })
      .filter((m): m is string => m !== null);
    if (messages.length > 0) return messages.join("; ");
  }
  return fallback;
}
